import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { take } from 'rxjs/operators';
import { WebsitePreviewComponent } from '../preview/website-preview/website-preview.component';
import { OttoChatComponent } from '../chat/otto-chat/otto-chat.component';
import { ChatService } from '../../services/chat.service';
import { CompanyIdService } from '../../services/company-id.service';
import { PreviewService } from '../../services/preview.service';
import { InlineContentEditorComponent } from '../preview/inline-content-editor/inline-content-editor.component';
import { InlineImageUploaderComponent } from '../preview/inline-image-uploader/inline-image-uploader.component';
import { TourOverlayComponent } from '../tour/tour-overlay.component';
import { GuidedTourService } from '../../services/guided-tour.service';
import { ChatPhase } from '../../interfaces/ai-content.interface';

// Enum for component states
enum ComponentState {
  CHAT = 'chat',
  CONTENT_EDITOR = 'content_editor',
  IMAGE_UPLOADER = 'image_uploader'
}

@Component({
  selector: 'app-builder',
  imports: [CommonModule, WebsitePreviewComponent, OttoChatComponent, InlineContentEditorComponent, InlineImageUploaderComponent, TourOverlayComponent],
  templateUrl: './builder.component.html',
  styleUrls: ['./builder.component.scss']
})
export class BuilderComponent implements OnInit, OnDestroy, AfterViewInit {
  // Make ComponentState enum available to template
  public ComponentState = ComponentState;
  
  isChatMinimized = false;
  private companyGuid: string | null = null;
  public hasValidGuid: boolean = false;
  public isLoading: boolean = true;

  // Component state management
  public currentComponentState: ComponentState = ComponentState.CHAT;
  
  // Inline editor state
  public currentEditRequest: any = null;
  public currentSupplierData: any = null;
  public currentSemanticContent: any = null;
  
  // Inline image uploader state
  public currentImageUploadRequest: any = null;
  
  private destroy$ = new Subject<void>();

  @ViewChild('websitePreview') websitePreviewComponent!: WebsitePreviewComponent;
  @ViewChild('ottoChat') ottoChatComponent!: OttoChatComponent;

  constructor(
    private chatService: ChatService,
    private route: ActivatedRoute,
    private router: Router,
    private companyIdService: CompanyIdService,
    private previewService: PreviewService,
    private guidedTourService: GuidedTourService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    console.log('Builder: Constructor called');
    // Set global reference for direct access (only in browser)
    if (isPlatformBrowser(this.platformId)) {
      (window as any).builderComponent = this;
      (window as any).tourService = this.guidedTourService;
    }
  }

    ngOnInit(): void {
    console.log('Builder: Component initializing...');
    
    // Handle encoded company ID query parameter
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      console.log('Builder: Query params received:', params);
      
      const encodedId = params['cid'] || null;
      console.log('Builder: Encoded ID:', encodedId);
      
      if (encodedId) {
        try {
          // Decode the company GUID from the encoded ID
          this.companyGuid = this.companyIdService.decode(encodedId);
          this.hasValidGuid = !!this.companyGuid;
          
          // Log decoding info for debugging
          const info = this.companyIdService.getEncodingInfo(encodedId);
          console.log('Builder: Encoded ID info:', info);
          console.log('Builder: GUID decoded successfully:', this.hasValidGuid);
          console.log('Builder: Decoded GUID:', this.companyGuid ? 'Valid' : 'Invalid');
        } catch (error) {
          console.error('Builder: Error decoding GUID:', error);
          this.companyGuid = null;
          this.hasValidGuid = false;
        }
      } else {
        console.log('Builder: No encoded ID provided');
        this.companyGuid = null;
        this.hasValidGuid = false;
      }
      
      this.isLoading = false;
      console.log('Builder: Final state - hasValidGuid:', this.hasValidGuid, 'isLoading:', this.isLoading);
      
      if (!this.hasValidGuid) {
        console.warn('Builder: No valid company ID found, redirecting to error page');
        this.router.navigate(['/error/guid-missing']);
        return;
      }
      
      // Set the GUID in the chat service if valid
      console.log('Builder: Setting company GUID in chat service:', this.companyGuid);
      this.chatService.setCompanyGuid(this.companyGuid!);
      
      // Start tour immediately after loading completes
      this.initializeGuidedTour();
    });

    // Subscribe to chat minimization state
    this.chatService.isMinimized$
      .pipe(takeUntil(this.destroy$))
      .subscribe(minimized => {
        this.isChatMinimized = minimized;
      });
  }

  // Removed onEditModeChange

  openInlineEditor(editRequest: any) {
    console.log('🔥 Builder: Content Editor opened for tag:', editRequest?.tagName);
    
    // Close image uploader if it's open
    if (this.currentComponentState === ComponentState.IMAGE_UPLOADER) {
      console.log('🔄 Builder: Closing image uploader to open content editor');
      this.currentImageUploadRequest = null;
    }
    
    this.currentEditRequest = editRequest;
    this.currentComponentState = ComponentState.CONTENT_EDITOR;
    
    // Use WebsitePreviewComponent methods to get the latest data
    if (this.websitePreviewComponent) {
      this.currentSupplierData = this.websitePreviewComponent.getCurrentSupplierData();
      this.currentSemanticContent = this.websitePreviewComponent.getCurrentSemanticContent();
      console.log('Builder: Retrieved current data from preview component');
    } else {
      this.currentSupplierData = {};
      this.currentSemanticContent = {};
      console.log('Builder: No preview component available, using empty data');
    }
    
    console.log('Builder: Inline editor should now be visible');
  }

  handleImageUploader(uploadRequest: any) {
    console.log('🔥 Builder: Image Uploader opened for tag:', uploadRequest?.tagName);
    
    // Close content editor if it's open
    if (this.currentComponentState === ComponentState.CONTENT_EDITOR) {
      console.log('🔄 Builder: Closing content editor to open image uploader');
      this.currentEditRequest = null;
    }
    
    this.currentImageUploadRequest = uploadRequest;
    this.currentComponentState = ComponentState.IMAGE_UPLOADER;
    
    // Use WebsitePreviewComponent methods to get the latest data
    if (this.websitePreviewComponent) {
      this.currentSupplierData = this.websitePreviewComponent.getCurrentSupplierData();
      this.currentSemanticContent = this.websitePreviewComponent.getCurrentSemanticContent();
      console.log('Builder: Retrieved current data from preview component');
    } else {
      this.currentSupplierData = {};
      this.currentSemanticContent = {};
      console.log('Builder: No preview component available, using empty data');
    }
    
    console.log('Builder: Image uploader visibility set to:', this.currentComponentState);
    console.log('Builder: Current image upload request:', this.currentImageUploadRequest);
    console.log('Builder: Image uploader should now be visible');
  }

  // Keep the old method for backward compatibility
  openImageUploader(uploadRequest: any) {
    this.handleImageUploader(uploadRequest);
  }

  async onContentUpdated(event: any) {
    // Handle content update by updating the preview service
    if (event && event.tagName && event.newContent) {
      console.log('Builder: Content updated for tag:', event.tagName, 'New content:', event.newContent);
      
      // Update the content in the preview service
      await this.previewService.updateContentInPreview(event.tagName, event.newContent, this.companyGuid || undefined);
      
      console.log('Builder: Content updated in preview service');
    }
    
    // Return to chat state
    this.returnToChat();
  }

  async onImageUpdated(event: any) {
    // Handle image update by updating the preview service
    if (event && event.tagName && event.imageBlob) {
      console.log('Builder: Image updated for tag:', event.tagName);
      
      // Only replace the image blob, don't change HTML src attributes
      // This preserves the original relative paths in HTML like "images/logo.png"
      const newBlobUrl = await this.previewService.replaceImageBlob(event.tagName, event.imageBlob, this.companyGuid || undefined);
      
      if (newBlobUrl) {
        console.log('Builder: Image blob replaced with new URL:', newBlobUrl);
        // The preview will automatically use the new blob when it resolves the relative path
      } else {
        console.warn('Builder: Failed to replace image blob');
      }
    } else if (event && event.tagName && event.newImageSrc && !event.imageBlob) {
      // Fallback: if no blob but we have a new image src (shouldn't happen with new flow)
      console.log('Builder: Fallback - updating image preview with src:', event.newImageSrc);
      await this.previewService.updateImageInPreview(event.tagName, event.newImageSrc, this.companyGuid || undefined);
    }
    
    // Return to chat state
    this.returnToChat();
  }

  onEditCancelled() {
    this.returnToChat();
  }

  onKeepOldContent() {
    // Similar to onKeepOldImage for image uploader
    console.log('Builder: Keep old content action');
    // Don't close the modal, just handle the keep old content action
  }

  onImageUploadCancelled() {
    this.returnToChat();
  }

  /**
   * Return to chat state and reset all component states
   */
  private returnToChat(): void {
    this.currentComponentState = ComponentState.CHAT;
    this.currentEditRequest = null;
    this.currentImageUploadRequest = null;
    console.log('Builder: Returned to chat state');
  }

  ngAfterViewInit(): void {
    // Just log that the component is initialized
    console.log('🔍 Builder: ngAfterViewInit called');
    console.log('🔍 Builder: websitePreviewComponent exists:', !!this.websitePreviewComponent);
    
    // Test if both event emitters exist
    if (this.websitePreviewComponent) {
      console.log('🔍 Builder: openInlineEditor emitter exists:', !!this.websitePreviewComponent.openInlineEditor);
      console.log('🔍 Builder: imageUploaderRequested emitter exists:', !!this.websitePreviewComponent.imageUploaderRequested);
    }

    // Monitor chat phase changes for editing tour
    this.chatService.currentPhase$
      .pipe(takeUntil(this.destroy$))
      .subscribe(phase => {
        this.handlePhaseChange(phase);
      });

    // Monitor preview service for AI content availability
    this.previewService.previewUpdate$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (data && this.previewService.hasAIContentBeenGenerated()) {
          this.checkForEditingTour();
        }
      });
  }

  /**
   * Initialize the guided tour system
   */
  private async initializeGuidedTour(): Promise<void> {
    if (!this.companyGuid) return;

    try {
      // Initialize tour service with company GUID
      await this.guidedTourService.initializeTour(this.companyGuid);
      
      // Check if user should see initial tour
      if (this.guidedTourService.shouldShowInitialTour()) {
        // Start tour immediately without waiting for components
        console.log('🎯 Starting tour immediately after loading...');
        this.guidedTourService.startInitialTour();
        
        // Enhance positioning as components become ready
        this.enhanceTourPositioning();
      }
    } catch (error) {
      console.error('Failed to initialize guided tour:', error);
    }
  }



  /**
   * Handle chat phase changes to save tour state
   */
  private async handlePhaseChange(phase: ChatPhase): Promise<void> {
    if (!this.companyGuid) return;

    // Save tour state whenever phase changes
    await this.guidedTourService.saveTourStateForUser(this.companyGuid);
  }

    /**
   * Enhance tour positioning as components become ready
   */
  private enhanceTourPositioning(): void {
    console.log('🎯 Enhancing tour positioning as components become ready...');
    
    // Monitor component readiness and improve positioning
    const checkComponents = () => {
      if (this.ottoChatComponent && this.ottoChatComponent.isChatReady()) {
        console.log('🎯 Components ready, enhancing tour positioning...');
        // Re-position tour with better accuracy
        this.guidedTourService.repositionCurrentStep();
      } else {
        // Continue checking every 100ms for up to 3 seconds
        setTimeout(checkComponents, 100);
      }
    };
    
    // Start checking after a short delay
    setTimeout(checkComponents, 100);
  }

  /**
   * Check if editing tour should be shown
   */
  private async checkForEditingTour(): Promise<void> {
    if (!this.companyGuid) return;

    // Check if user should see editing tour
    if (this.guidedTourService.shouldShowEditingTour()) {
      // Wait a bit for the edit buttons to appear
      setTimeout(() => {
        this.guidedTourService.startEditingTour();
      }, 1000);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Debug method to manually trigger tour (for testing)
   */
  debugStartTour(): void {
    console.log('🔧 Debug: Manually starting tour...');
    this.guidedTourService.debugStartTour('initial');
  }
} 